import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Select from "react-select";
import { Row, Col } from "react-bootstrap";
import RetreatRoasterApi from "../api/retreatRoasterApi";
import DeleteModel from "./DeleteModel";

const customSelectStyles = {
  control: (provided) => ({
    ...provided,
    minHeight: "50px",
    fontSize: "16px",
    borderRadius: "10px",
  }),
  menu: (provided) => ({
    ...provided,
    fontSize: "16px",
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 2000,
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "16px",
    backgroundColor: state.isSelected
      ? "#d97706"
      : state.isFocused
        ? "#fff7ed"
        : "white",
    color: state.isSelected ? "white" : "black",
  }),
};

const renderCoupleNames = (couple) => {
  if (!couple) return null;

  const hisFirst = couple.hisFirstName?.trim();
  const herFirst = couple.herFirstName?.trim();
  const hisLast = couple.hisLastName?.trim();
  const herLast = couple.herLastName?.trim();

  let name = "";

  if (hisFirst && herFirst) {
    const lastName = hisLast || herLast || "";
    name = `${hisFirst} & ${herFirst} ${lastName}`.trim();
  } else if (hisFirst) {
    name = `${hisFirst} ${hisLast || ""}`.trim();
  } else if (herFirst) {
    name = `${herFirst} ${herLast || ""}`.trim();
  }

  const emails = [couple.hisEmail, couple.herEmail].filter(Boolean).join(" & ");

  if (!name && !emails) return null;

  return `${name}${emails ? ` (${emails})` : ""}`.trim();
};

function RetreatTeamSection({
  allCoupleData,
  priestsList = [],
  rolesDataList,
  roles,
  retreatId,
  getRolesOfRetreats,
  onTeamChange,
  onPeopleRefresh,
  setEditIndex,
  editIndex,
}) {
  const retreatRoasterApi = new RetreatRoasterApi();
  const [roleSelect, setRoleSelect] = useState(null);
  const [roleGreenTick, setRoleGreenTick] = useState(false);
  const [coupleSelectForRole, setCoupleSelectForRole] = useState(null);
  const [priestSelectForRole, setPriestSelectForRole] = useState(null);
  const [removeCoupleModal, setRemoveCoupleModal] = useState(false);

  const isSpiritualDirector =
    roleSelect && roleSelect.label === "Spiritual Director";

  const onPeopleRefreshRef = useRef(onPeopleRefresh);
  onPeopleRefreshRef.current = onPeopleRefresh;

  useEffect(() => {
    const refreshTeamPeople = () => {
      if (document.visibilityState !== "visible") return;
      onPeopleRefreshRef.current?.();
    };
    window.addEventListener("focus", refreshTeamPeople);
    document.addEventListener("visibilitychange", refreshTeamPeople);
    return () => {
      window.removeEventListener("focus", refreshTeamPeople);
      document.removeEventListener("visibilitychange", refreshTeamPeople);
    };
  }, []);

  const openAddPersonPage = () => {
    const path = isSpiritualDirector
      ? "/priest-list?add=1"
      : "/manageCouples?add=1";
    window.open(path, "_blank", "noopener,noreferrer");
  };

  const personOptions = isSpiritualDirector
    ? (priestsList || []).map((p) => ({
        label:
          [p.title, p.firstName, p.lastName].filter(Boolean).join(" ") +
          (p.parish ? ` (${p.parish})` : ""),
        value: p.id,
      }))
    : allCoupleData;

  const handleSelectOption = (val, selection) => {
    if (selection === "forRole") {
      setRoleSelect(val);
      setCoupleSelectForRole(null);
      setPriestSelectForRole(null);
    } else if (selection === "coupleForRole") {
      setCoupleSelectForRole(val);
    } else if (selection === "priestForRole") {
      setPriestSelectForRole(val);
    }
  };

  const handleSelectSubmit = async () => {
    if (!retreatId || !roleSelect?.value) {
      toast.error("Please select Role.");
      return;
    }
    if (isSpiritualDirector) {
      if (!priestSelectForRole?.value) {
        toast.error("Please select Priest.");
        return;
      }
    } else if (!coupleSelectForRole?.value) {
      toast.error("Please select Couple.");
      return;
    }

    const submitData = {
      roleId: roleSelect.value,
      retreatId,
      coupleId: isSpiritualDirector ? null : coupleSelectForRole.value,
      priestId: isSpiritualDirector ? priestSelectForRole.value : null,
    };

    setRoleGreenTick(true);
    try {
      const res = await retreatRoasterApi.createLeads(submitData);
      if (res.success) {
        await Promise.all([
          getRolesOfRetreats?.(retreatId),
          onTeamChange?.(retreatId),
        ]);
        toast.success(res.msg);
        setRoleSelect(null);
        setCoupleSelectForRole(null);
        setPriestSelectForRole(null);
      } else {
        toast.error(res.msg);
      }
    } catch (error) {
      toast.error(error?.message || "Error");
    }
    setTimeout(() => setRoleGreenTick(false), 2000);
  };

  const handlerRemoveShow = (member) => {
    setEditIndex(member);
    setRemoveCoupleModal(true);
  };

  const handleRemoveClose = () => {
    setRemoveCoupleModal(false);
  };

  const removeCoupleFromRetreat = () => {
    retreatRoasterApi
      .removeCoupleFromRetreat({
        coupleId: editIndex?.coupleId || null,
        priestId: editIndex?.priestId || null,
        retreatId,
      })
      .then((res) => {
        toast.success(res.msg);
        getRolesOfRetreats(retreatId);
        handleRemoveClose();
        setEditIndex("");
        setRemoveCoupleModal(false);
      })
      .catch((error) => {
        console.error(error);
        toast.error("An error occurred while removing team member.");
      });
  };

  const leadCouple = renderCoupleNames(roles?.leadCouple);
  const coLeadCouple = renderCoupleNames(roles?.coLeadCouple);
  const mentorCouple = renderCoupleNames(roles?.mentor);
  const spiritualDirector = renderCoupleNames(roles?.spiritualDirector);
  const spiritualMentor = renderCoupleNames(roles?.spiritualMentor);

  return (
    <>
      <Row className="align-items-center g-3 cr-team-picker mt-3">
        <Col md={4}>
          <Select
            styles={customSelectStyles}
            id="roles"
            name="roles"
            options={rolesDataList}
            value={roleSelect}
            onChange={(val) => handleSelectOption(val, "forRole")}
            isSearchable
            placeholder="Select Role"
            menuPlacement="bottom"
            menuPosition="fixed"
            menuPortalTarget={document.body}
          />
        </Col>
        <Col md={6}>
          <div className="cr-person-picker">
            <div className="cr-person-select">
              {isSpiritualDirector ? (
                <Select
                  styles={customSelectStyles}
                  id="priest"
                  name="priest"
                  options={personOptions}
                  value={priestSelectForRole}
                  onChange={(val) => handleSelectOption(val, "priestForRole")}
                  isSearchable
                  placeholder="Select Priest"
                  menuPlacement="bottom"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                />
              ) : (
                <Select
                  styles={customSelectStyles}
                  id="couple"
                  name="couple"
                  options={personOptions}
                  value={coupleSelectForRole}
                  onChange={(val) => handleSelectOption(val, "coupleForRole")}
                  isSearchable
                  placeholder="Select Couple"
                  menuPlacement="bottom"
                  menuPosition="fixed"
                  menuPortalTarget={document.body}
                />
              )}
            </div>
            <button
              type="button"
              className="cr-add-person-btn"
              onClick={openAddPersonPage}
              title={
                isSpiritualDirector ? "Add New Priest" : "Add New Couple"
              }
              aria-label={
                isSpiritualDirector ? "Add New Priest" : "Add New Couple"
              }
            >
              <i
                className={
                  isSpiritualDirector
                    ? "mdi mdi-account-plus"
                    : "mdi mdi-account-multiple-plus"
                }
                aria-hidden="true"
              />
            </button>
          </div>
        </Col>
        {roleGreenTick ? (
          <Col md={2} className="text-center">
            <i className="mdi mdi-check btn-icon-prepend mt-0 mb-3 plus-icon-role" />
          </Col>
        ) : (
          <Col md={2} className="text-center" title="Add Retreat Team">
            <i
              type="button"
              onClick={handleSelectSubmit}
              className="mdi mdi-plus-circle btn-icon-prepend mt-0 mb-3 plus-icon-role"
              style={{ cursor: "pointer" }}
            />
          </Col>
        )}
      </Row>

      <hr className="my-4" />

      <div className="selected-couples mt-3">
        {leadCouple && (
          <div className="cr-team-member d-flex justify-content-between align-items-center border rounded p-2 mb-2">
            <span>
              <strong>Lead Couple:</strong> {leadCouple}
            </span>
            <i
              className="mdi mdi-delete-forever text-danger fs-4"
              onClick={() => handlerRemoveShow(roles?.leadCouple)}
              title="Remove Couple"
              style={{ cursor: "pointer" }}
            />
          </div>
        )}
        {coLeadCouple && (
          <div className="cr-team-member d-flex justify-content-between align-items-center border rounded p-2 mb-2">
            <span>
              <strong>Co-Lead Couple:</strong> {coLeadCouple}
            </span>
            <i
              className="mdi mdi-delete-forever text-danger fs-4"
              onClick={() => handlerRemoveShow(roles?.coLeadCouple)}
              title="Remove Couple"
              style={{ cursor: "pointer" }}
            />
          </div>
        )}
        {mentorCouple && (
          <div className="cr-team-member d-flex justify-content-between align-items-center border rounded p-2 mb-2">
            <span>
              <strong>Mentor Couple:</strong> {mentorCouple}
            </span>
            <i
              className="mdi mdi-delete-forever text-danger fs-4"
              onClick={() => handlerRemoveShow(roles?.mentor)}
              title="Remove Couple"
              style={{ cursor: "pointer" }}
            />
          </div>
        )}
        {spiritualMentor && (
          <div className="cr-team-member d-flex justify-content-between align-items-center border rounded p-2 mb-2">
            <span>
              <strong>Spiritual Mentor:</strong> {spiritualMentor}
            </span>
            <i
              className="mdi mdi-delete-forever text-danger fs-4"
              onClick={() => handlerRemoveShow(roles?.spiritualMentor)}
              title="Remove Couple"
              style={{ cursor: "pointer" }}
            />
          </div>
        )}
        {spiritualDirector && (
          <div className="cr-team-member d-flex justify-content-between align-items-center border rounded p-2 mb-2">
            <span>
              <strong>Spiritual Director (Priest/Deacon):</strong> {spiritualDirector}
            </span>
            <i
              className="mdi mdi-delete-forever text-danger fs-4"
              onClick={() => handlerRemoveShow(roles?.spiritualDirector)}
              title="Remove Couple"
              style={{ cursor: "pointer" }}
            />
          </div>
        )}
      </div>

      {removeCoupleModal ? (
        <DeleteModel
          show={removeCoupleModal}
          content={
            <>
              <h5 className="mt-4 fw-bold">Retreat Team</h5>
              <div className="p-3 border rounded-3 bg-light shadow-sm">
                {editIndex && (
                  <>
                    <div className="mb-2">
                      <span className="fw-bold">Role:</span>{" "}
                      <span className="text-muted">{editIndex.role}</span>
                    </div>
                    <div className="mb-2">
                      <span className="fw-bold">
                        {editIndex.priestId ? "Priest:" : "Couple:"}
                      </span>{" "}
                      {`${editIndex.hisFirstName || ""}${
                        editIndex.herFirstName ? " & " : ""
                      }${editIndex.herFirstName || ""} ${
                        editIndex.hisLastName || ""
                      }`.trim()}
                    </div>
                  </>
                )}
              </div>
            </>
          }
          handleClose={handleRemoveClose}
          DeleteData={removeCoupleFromRetreat}
        />
      ) : null}
    </>
  );
}

export default RetreatTeamSection;
